// modules/facturacion/components/EditarFacturaModal.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Alert, Box, Divider, Grid,
  Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Checkbox, IconButton, Paper, Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import * as svc from "../services/facturacionService";

const MESES_STR = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
// Misma nocion que en PrefacturaServicioModal: la "linea principal" de un
// concepto (la que refleja la Descripcion interna en su Descripcion Comercial)
// es la de orden === 1; si ninguna la tiene, se usa la primera.
const getPrincipalIdx = (reglas) => {
  const idx = (reglas || []).findIndex((r) => Number(r.orden) === 1);
  return idx >= 0 ? idx : 0;
};

const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz"];
const VENTAS = ["Alquiler", "Venta", "Franquicia"];
const TIPOS_DOCUMENTO = [
  { codigo: 1, nombre: "CI" },
  { codigo: 2, nombre: "CEX" },
  { codigo: 3, nombre: "PAS" },
  { codigo: 4, nombre: "OD" },
  { codigo: 5, nombre: "NIT" },
];

const now = new Date();
const DEFAULT_MES = MESES_STR[now.getMonth()];
const DEFAULT_YEAR = String(now.getFullYear());

const mesActualIdx  = now.getMonth();
const yearActual     = now.getFullYear();
const mesPrevioIdx   = mesActualIdx === 0 ? 11 : mesActualIdx - 1;
const yearPrevio      = mesActualIdx === 0 ? yearActual - 1 : yearActual;

// Mismo criterio que al registrar una pre-factura nueva: si el cliente paga
// adelantado, el periodo por defecto es el mes actual; si paga vencido, el anterior.
const getPeriodoDefault = (pago) => {
  const esAdelantado = pago === "adelantado";
  return {
    mes: esAdelantado ? MESES_STR[mesActualIdx] : MESES_STR[mesPrevioIdx],
    year: String(esAdelantado ? yearActual : yearPrevio),
  };
};

const Th = styled(TableCell)({
  fontWeight: 700,
  backgroundColor: "#008080",
  color: "white",
  padding: "3px 5px",
  fontSize: "0.7rem",
  whiteSpace: "nowrap",
});

const compactTableSx = {
  "& .MuiTableCell-root": { py: 0.2, px: 0.6 },
};

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 24, height: 24, fontSize: "0.74rem" },
  "& .MuiInputBase-input": { py: 0.2, px: 0.6 },
  "& .MuiInputLabel-root": { fontSize: "0.72rem" },
  "& .MuiSelect-select": { py: "2px" },
};

const money = (value) => Number(value || 0).toFixed(2);
const parseDecimal = (value, fallback = 0) => {
  if (value === "" || value === null || value === undefined) return fallback;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : fallback;
};
// Cantidad: solo enteros. Precios/descuentos: hasta 2 decimales.
const allowIntegerInput = (value) => /^\d*$/.test(String(value || ""));
const allowPriceInput = (value) => /^-?\d*([.,]\d{0,2})?$/.test(String(value || ""));
const stripCuotaDetalle = (detalle = "") =>
  String(detalle).replace(/\s*\.?\s*Cuota No:\s*[^.]+$/i, "").trim();
const getItemValue = (item) => String(item?.id ?? item?.id_fac_item ?? "");
const getItemLabel = (item) => item?.item || item?.nombre || item?.descripcion || getItemValue(item);

const normalizeDetalleForm = (row = {}) => ({
  ...row,
  detalle: stripCuotaDetalle(row.detalle || ""),
  id_fac_item: row.id_fac_item || row.tipo || "",
  tipo: row.tipo || row.id_fac_item || "",
  mostrar_cuota: row.mostrar_cuota || "SI",
  mes_servicio: row.mes_servicio || DEFAULT_MES,
  year: row.year || DEFAULT_YEAR,
  precio_unitario: row.precio_unitario ?? 0,
  descuento_interno: row.descuento_interno ?? 0,
  cantidad: row.cantidad ?? 1,
});

const validarDetalleForm = (row = {}) => {
  const cantidad = parseDecimal(row.cantidad);
  const precio = parseDecimal(row.precio_unitario);
  const descuento = parseDecimal(row.descuento_interno);
  const tipo = Number(row.tipo || row.id_fac_item);
  const subtotalBruto = cantidad * precio;
  const subtotal = subtotalBruto - descuento;

  if (!row.id_fac_item || !Number.isFinite(tipo) || tipo <= 0) return "El item es obligatorio";
  if (!Number.isFinite(cantidad) || cantidad <= 0) return "La cantidad debe ser mayor a 0";
  if (!Number.isFinite(precio) || precio < 0) return "El precio unitario no puede ser negativo";
  if (!Number.isFinite(descuento) || descuento < 0) return "El descuento no puede ser negativo";
  if (subtotal === 0) return "El subtotal no debe ser 0";
  if (tipo === 5 && subtotal > 0) return "El monto del descuento debe resultar en subtotal negativo";

  if (descuento > 0 && tipo !== 5) {
    if (descuento > subtotalBruto && subtotalBruto >= 0) return "El descuento no puede ser mayor al subtotal";
    const descuentoPorUnidad = Number((descuento / cantidad).toFixed(10));
    const decimales = (descuentoPorUnidad.toString().split(".")[1] || "").length;
    if (decimales > 2) {
      const valorAproximado = Math.round(descuentoPorUnidad * 100) / 100 * cantidad;
      return `DESCUENTO ${descuento} NO ES VALIDO. Un valor aproximado valido es ${valorAproximado}`;
    }
  }
  return "";
};

const isDiscountPerUnitValid = (descuento, cantidad) => {
  const unitario = Number(descuento) / Number(cantidad || 1);
  return Math.abs((unitario * 100) - Math.round(unitario * 100)) < 1e-8;
};

const getValidGroupDiscountSuggestions = (descuentoBase, cantidad, reglas = []) => {
  const centro = Math.round(parseDecimal(descuentoBase) * 100);
  const encontrados = [];
  const vistos = new Set();
  const isValid = (valor) => reglas.every((regla) => {
    const pct = Number(regla.porcentaje || regla.porcentaje_regla || 100) / 100;
    return isDiscountPerUnitValid(Math.round(valor * pct * 100) / 100, cantidad);
  });

  for (let offset = 0; offset <= 2000 && encontrados.length < 4; offset += 1) {
    const opciones = offset === 0 ? [centro] : [centro - offset, centro + offset];
    for (const cents of opciones) {
      if (cents < 0 || vistos.has(cents)) continue;
      vistos.add(cents);
      const valor = cents / 100;
      if (isValid(valor)) {
        encontrados.push(valor);
        if (encontrados.length >= 4) break;
      }
    }
  }

  return encontrados
    .sort((a, b) => Math.abs(a - descuentoBase) - Math.abs(b - descuentoBase))
    .slice(0, 2);
};

const validarDescuentoConceptoInterno = (descuentoBase, cantidad, reglas = []) => {
  const descuento = parseDecimal(descuentoBase);
  if (descuento <= 0 || !reglas.length) return "";
  const valido = reglas.every((regla) => {
    const pct = Number(regla.porcentaje || regla.porcentaje_regla || 100) / 100;
    return isDiscountPerUnitValid(Math.round(descuento * pct * 100) / 100, cantidad);
  });
  if (valido) return "";

  const sugerencias = getValidGroupDiscountSuggestions(descuento, cantidad, reglas);
  const texto = sugerencias.length
    ? ` Valores aproximados validos: ${sugerencias.map((v) => v.toFixed(2)).join(" o ")}`
    : "";
  return `DESCUENTO INTERNO ${descuento.toFixed(2)} NO ES VALIDO.${texto}`;
};

// ── Fila editable de linea fiscal (siempre editable, sin toggle) ──
// Las lineas generadas desde un concepto interno (soloDetalle) solo permiten
// editar la Descripcion Comercial; el resto de campos vienen del concepto.
const LineaRow = ({ l, facItems, onChange, onTipoChange, onDelete, disabled, soloDetalle }) => {
  const cant = parseDecimal(l.cantidad);
  const pu = parseDecimal(l.precio_unitario);
  const desc = parseDecimal(l.descuento_interno);
  const subtotal = (cant * pu) - desc;
  const camposDisabled = disabled || soloDetalle;

  return (
    <TableRow hover sx={{ bgcolor: subtotal < 0 ? "rgba(211, 47, 47, 0.04)" : "inherit" }}>
      <TableCell>
        <TextField size="small" type="text" sx={{ ...compactFieldSx, width: 60 }}
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          value={l.cantidad} disabled={camposDisabled}
          onChange={(e) => { if (allowIntegerInput(e.target.value)) onChange(l._localId, "cantidad", e.target.value); }} />
      </TableCell>
      <TableCell>
        <TextField select size="small" sx={{ ...compactFieldSx, width: 112 }}
          value={String(l.id_fac_item || l.tipo || "")} disabled={camposDisabled}
          required error={!camposDisabled && !l.id_fac_item}
          onChange={(e) => onTipoChange(l._localId, e.target.value)}>
          <MenuItem value="">Seleccione</MenuItem>
          {facItems.map((item) => (
            <MenuItem key={getItemValue(item)} value={getItemValue(item)}>{getItemLabel(item)}</MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell>
        <TextField size="small" fullWidth sx={compactFieldSx}
          value={l.detalle || ""} disabled={disabled}
          onChange={(e) => onChange(l._localId, "detalle", e.target.value)} />
      </TableCell>
      <TableCell align="center">
        <Checkbox size="small" sx={{ p: 0 }}
          checked={(l.mostrar_cuota || "SI") === "SI"} disabled={camposDisabled}
          onChange={(e) => onChange(l._localId, "mostrar_cuota", e.target.checked ? "SI" : "NO")} />
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", gap: 0.3 }}>
          <TextField select size="small" sx={{ ...compactFieldSx, width: 85 }}
            value={l.mes_servicio || ""} disabled={camposDisabled}
            onChange={(e) => onChange(l._localId, "mes_servicio", e.target.value)}>
            {MESES_STR.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
          <TextField size="small" type="text" sx={{ ...compactFieldSx, width: 55 }}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            value={l.year || ""} disabled={camposDisabled}
            onChange={(e) => onChange(l._localId, "year", e.target.value)} />
        </Box>
      </TableCell>
      <TableCell>
        <TextField size="small" type="text" sx={{ ...compactFieldSx, width: 80 }}
          inputProps={{ inputMode: "decimal" }}
          value={l.precio_unitario} disabled={camposDisabled}
          onChange={(e) => { if (allowPriceInput(e.target.value)) onChange(l._localId, "precio_unitario", e.target.value); }} />
      </TableCell>
      <TableCell>
        <TextField size="small" type="text" sx={{ ...compactFieldSx, width: 75 }}
          inputProps={{ inputMode: "decimal" }}
          value={l.descuento_interno ?? ""} disabled={camposDisabled}
          onChange={(e) => { if (allowPriceInput(e.target.value)) onChange(l._localId, "descuento_interno", e.target.value); }} />
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.76rem" }}>
        {subtotal.toFixed(2)}
      </TableCell>
      <TableCell align="center">
        {!soloDetalle && (
          <IconButton size="small" color="error" onClick={() => onDelete(l._localId)} disabled={disabled}>
            <DeleteIcon fontSize="inherit" />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );
};

// ── Fila editable de concepto interno (siempre editable, sin toggle) ──
// El Tipo (metodo_calculo) solo se puede elegir/editar en conceptos nuevos —
// uno ya existente se muestra fijo; si se quiere cambiar de tipo hay que
// eliminar el concepto y agregar uno nuevo.
const GrupoRow = ({ g, conceptos, onChange, onTipoChange, onDelete, disabled }) => {
  const cant = parseDecimal(g.cantidad);
  const base = parseDecimal(g.precio_unitario_base);
  const desc = parseDecimal(g.descuento_interno);
  const total = (cant * base) - desc;
  const esNuevo = g._origin === "new";

  return (
    <TableRow hover sx={{ verticalAlign: "top" }}>
      <TableCell align="center">
        <TextField size="small" type="text" sx={{ ...compactFieldSx, width: 60 }}
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          value={g.cantidad} disabled={disabled}
          onChange={(e) => { if (allowIntegerInput(e.target.value)) onChange(g._localId, "cantidad", e.target.value); }} />
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {esNuevo ? (
            <TextField select size="small" sx={{ ...compactFieldSx, width: 190, flexShrink: 0 }}
              value={g.metodo_calculo || ""} disabled={disabled}
              onChange={(e) => onTipoChange(g._localId, e.target.value)}>
              {!g.metodo_calculo && <MenuItem value=""><em>Seleccione...</em></MenuItem>}
              {conceptos.map((c) => <MenuItem key={c.metodo_calculo} value={c.metodo_calculo}>{c.metodo_calculo}</MenuItem>)}
            </TextField>
          ) : (
            <Typography variant="body2" sx={{ width: 190, flexShrink: 0, fontWeight: 700, fontSize: "0.76rem", pt: 0.5 }}>
              {g.metodo_calculo}
            </Typography>
          )}
          <TextField size="small" fullWidth sx={compactFieldSx}
            value={g.descripcion_interna || ""} disabled={disabled}
            onChange={(e) => onChange(g._localId, "descripcion_interna", e.target.value)} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.65rem", mt: 0.2 }}>
          {g.lineas.map((l) => `${Number(l.porcentaje || 100)}% ${l.detalle_hippo || l.detalle || ""}`).join(" | ")}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <TextField size="small" type="text" sx={{ ...compactFieldSx, width: 82 }}
          inputProps={{ inputMode: "decimal" }}
          value={g.precio_unitario_base} disabled={disabled}
          onChange={(e) => { if (allowPriceInput(e.target.value)) onChange(g._localId, "precio_unitario_base", e.target.value); }} />
      </TableCell>
      <TableCell align="right">
        <TextField size="small" type="text" sx={{ ...compactFieldSx, width: 78 }}
          inputProps={{ inputMode: "decimal" }}
          value={g.descuento_interno} disabled={disabled}
          onChange={(e) => { if (allowPriceInput(e.target.value)) onChange(g._localId, "descuento_interno", e.target.value); }} />
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: "flex", gap: 0.3, alignItems: "center", justifyContent: "center" }}>
          <Checkbox size="small" sx={{ p: 0 }}
            checked={(g.mostrar_cuota || "SI") === "SI"} disabled={disabled}
            onChange={(e) => onChange(g._localId, "mostrar_cuota", e.target.checked ? "SI" : "NO")} />
          <TextField select size="small" sx={{ ...compactFieldSx, width: 82 }}
            value={g.mes_servicio || ""} disabled={disabled}
            onChange={(e) => onChange(g._localId, "mes_servicio", e.target.value)}>
            {MESES_STR.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
          <TextField size="small" type="text" sx={{ ...compactFieldSx, width: 52 }}
            inputProps={{ inputMode: "numeric" }}
            value={g.year || ""} disabled={disabled}
            onChange={(e) => onChange(g._localId, "year", e.target.value)} />
        </Box>
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: 800 }}>{money(total)}</TableCell>
      <TableCell align="center">
        <IconButton size="small" color="error" onClick={() => onDelete(g._localId)} disabled={disabled}>
          <DeleteIcon fontSize="inherit" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const EditarFacturaModal = ({ open, onClose, cod, factura, detalles, detalleGrupos, facItems, reglasDetalle, onSaved }) => {
  const [headerForm, setHeaderForm] = useState({});
  const [lineas, setLineas] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [previewDetalleOverrides, setPreviewDetalleOverrides] = useState({});
  const [addingLinea, setAddingLinea] = useState(false);
  const [nuevaLinea, setNuevaLinea] = useState(normalizeDetalleForm({ detalle: "" }));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveReport, setSaveReport] = useState(null);

  const initKeyRef = useRef("");
  const headerSnapshotRef = useRef({});
  const lineasSnapshotRef = useRef([]);
  const gruposSnapshotRef = useRef([]);
  const localIdRef = useRef(0);
  const nextLocalId = () => `local-${++localIdRef.current}`;

  useEffect(() => {
    if (!open) { initKeyRef.current = ""; return; }
    const initKey = String(cod);
    if (initKeyRef.current === initKey) return;
    initKeyRef.current = initKey;

    const header = {
      nit: factura?.nit || "",
      fac_Nombre: factura?.fac_Nombre || "",
      tipo_documento_identidad: factura?.tipo_documento_identidad || 5,
      mes: factura?.mes || DEFAULT_MES,
      year: factura?.year || DEFAULT_YEAR,
      sucursal: factura?.sucursal || "",
      venta: factura?.venta || "Alquiler",
    };
    setHeaderForm(header);
    headerSnapshotRef.current = header;

    // detalle-grupos trae, por cada linea real (mismo "cod" que en /detalles), el
    // porcentaje de su regla — lo necesitamos para recalcular en vivo el precio de
    // esa linea cuando se edita el concepto interno al que pertenece.
    const porcentajePorCod = new Map();
    (Array.isArray(detalleGrupos) ? detalleGrupos : []).forEach((g) => {
      (Array.isArray(g.lineas) ? g.lineas : []).forEach((l) => {
        if (l.cod !== undefined) porcentajePorCod.set(l.cod, Number(l.porcentaje || 0));
      });
    });

    const lineasIniciales = (Array.isArray(detalles) ? detalles : [])
      .map((d) => ({
        ...normalizeDetalleForm(d),
        _localId: nextLocalId(),
        _origin: "existing",
        cod: d.cod,
        _esDeGrupo: !!d.id_detalle_grupo,
        _idDetalleGrupo: d.id_detalle_grupo || null,
        _porcentaje: porcentajePorCod.has(d.cod) ? porcentajePorCod.get(d.cod) : null,
      }));
    setLineas(lineasIniciales);
    lineasSnapshotRef.current = lineasIniciales;

    const gruposIniciales = (Array.isArray(detalleGrupos) ? detalleGrupos : []).map((g) => ({
      _localId: nextLocalId(),
      _origin: "existing",
      id: g.id,
      metodo_calculo: g.metodo_calculo || "",
      descripcion_interna: g.descripcion_interna || "",
      cantidad: String(g.cantidad ?? 1),
      precio_unitario_base: String(g.precio_unitario_base ?? 0),
      descuento_interno: String(g.descuento_interno ?? 0),
      mes_servicio: g.mes_servicio || DEFAULT_MES,
      year: g.year || DEFAULT_YEAR,
      mostrar_cuota: g.mostrar_cuota || "SI",
      lineas: Array.isArray(g.lineas) ? g.lineas : [],
    }));
    setGrupos(gruposIniciales);
    gruposSnapshotRef.current = gruposIniciales;

    setAddingLinea(false);
    setPreviewDetalleOverrides({});
    setError(null);
    setSaveReport(null);
  }, [open, cod, factura, detalles, detalleGrupos]);

  const bloqueada = factura?.pre_fac === "no" || factura?.estado === "X";
  const tieneDetalleInterno = grupos.length > 0;
  const bloqueaLineasFiscales = bloqueada || tieneDetalleInterno;
  const tieneReglasInternasConfiguradas = (Array.isArray(reglasDetalle) ? reglasDetalle : [])
    .some((concepto) => Array.isArray(concepto?.reglas) && concepto.reglas.length > 0);

  const conceptosDisponiblesNuevos = (Array.isArray(reglasDetalle) ? reglasDetalle : [])
    .filter((concepto) => Array.isArray(concepto?.reglas) && concepto.reglas.length > 0);

  // ── Lineas ──
  const updateLineaField = (localId, field, value) => {
    // Las filas de vista previa (conceptos nuevos, se recalculan en cada render)
    // no viven en "lineas" — la descripcion comercial que se edite ahi se guarda
    // aparte y se reaplica al reconstruir la fila y al armar el payload final.
    if (String(localId).startsWith("preview-") && field === "detalle") {
      setPreviewDetalleOverrides((prev) => ({ ...prev, [localId]: value }));
      return;
    }
    setLineas((prev) => prev.map((l) => (l._localId === localId ? { ...l, [field]: value } : l)));
  };
  const handleLineaTipoChange = (localId, value) => {
    const item = facItems.find((f) => getItemValue(f) === String(value));
    setLineas((prev) => prev.map((l) => (l._localId === localId
      ? { ...l, id_fac_item: value, tipo: value, detalle: item ? getItemLabel(item) : l.detalle }
      : l)));
  };
  const removeLinea = (localId) => setLineas((prev) => prev.filter((l) => l._localId !== localId));

  const startAddLinea = () => {
    setNuevaLinea(normalizeDetalleForm({ detalle: "", mes_servicio: headerForm.mes, year: headerForm.year }));
    setAddingLinea(true);
  };
  const handleNuevaLineaTipoChange = (value) => {
    const item = facItems.find((f) => getItemValue(f) === String(value));
    setNuevaLinea((p) => ({ ...p, id_fac_item: value, tipo: value, detalle: item ? getItemLabel(item) : p.detalle }));
  };
  const handleAddLinea = () => {
    if (!nuevaLinea.detalle) return setError("El detalle es requerido");
    if (!nuevaLinea.id_fac_item || !nuevaLinea.tipo) return setError("El item es obligatorio");
    if (!nuevaLinea.mes_servicio) return setError("El mes de servicio es requerido");
    const validationError = validarDetalleForm(nuevaLinea);
    if (validationError) return setError(validationError);

    setLineas((prev) => [...prev, { ...nuevaLinea, _localId: nextLocalId(), _origin: "new" }]);
    setAddingLinea(false);
    setError(null);
  };

  // ── Conceptos internos ──
  const updateGrupoField = (localId, field, value) => {
    setGrupos((prev) => prev.map((g) => (g._localId === localId ? { ...g, [field]: value } : g)));
    if (field === "descripcion_interna") {
      // Igual que al registrar una pre-factura: la Descripcion interna se
      // refleja tal cual en la Descripcion Comercial de la linea fiscal principal.
      const grupo = grupos.find((g) => g._localId === localId);
      const idx = getPrincipalIdx(grupo?.lineas);
      setPreviewDetalleOverrides((prev) => ({ ...prev, [`preview-${localId}-${idx}`]: value }));
    }
  };
  const removeGrupo = (localId) => setGrupos((prev) => prev.filter((g) => g._localId !== localId));

  // Inserta directamente una fila en blanco editable (igual que al registrar una
  // pre-factura nueva) en vez de pedir elegir el tipo en un panel aparte y recien
  // ahi crear la fila — el Tipo se elige y edita en la misma fila de la tabla.
  const handleAgregarConceptoBlanco = () => {
    const periodo = getPeriodoDefault(factura?.pago);
    setGrupos((prev) => [...prev, {
      _localId: nextLocalId(),
      _origin: "new",
      id: null,
      metodo_calculo: "",
      descripcion_interna: "",
      cantidad: "1",
      precio_unitario_base: "0",
      descuento_interno: "0",
      mes_servicio: periodo.mes,
      year: periodo.year,
      mostrar_cuota: "SI",
      lineas: [],
    }]);
  };

  const handleGrupoTipoChange = (localId, metodoCalculo) => {
    const concepto = conceptosDisponiblesNuevos.find((c) => String(c.metodo_calculo) === String(metodoCalculo));
    const nuevasLineas = concepto?.reglas ? concepto.reglas.map((regla) => ({ ...regla })) : null;
    setGrupos((prev) => prev.map((g) => (g._localId === localId
      ? {
          ...g,
          metodo_calculo: metodoCalculo,
          descripcion_interna: concepto?.descripcion_interna || g.descripcion_interna,
          lineas: nuevasLineas || g.lineas,
        }
      : g)));
    // Igual que al registrar: al elegir el tipo, la linea principal ya arranca
    // con la Descripcion Comercial igual a la Descripcion interna por defecto.
    if (concepto?.descripcion_interna && nuevasLineas) {
      const idx = getPrincipalIdx(nuevasLineas);
      setPreviewDetalleOverrides((prev) => ({ ...prev, [`preview-${localId}-${idx}`]: concepto.descripcion_interna }));
    }
  };

  const totalConceptosInternos = grupos.reduce(
    (acc, g) => acc + ((parseDecimal(g.cantidad) * parseDecimal(g.precio_unitario_base)) - parseDecimal(g.descuento_interno)), 0);

  // ── Guardado unificado ──
  const buildLineaPayload = (l) => ({
    cantidad: parseDecimal(l.cantidad),
    precio_unitario: parseDecimal(l.precio_unitario),
    id_fac_item: l.id_fac_item || l.tipo || null,
    id_regla_detalle: l.id_regla_detalle || null,
    id_detalle_grupo: l.id_detalle_grupo || null,
    tipo: l.tipo || l.id_fac_item || "",
    detalle: stripCuotaDetalle(l.detalle),
    descripcion_interna: l.descripcion_interna || null,
    mostrar_cuota: l.mostrar_cuota || "SI",
    mes_servicio: l.mes_servicio,
    year: l.year,
    descuento_interno: parseDecimal(l.descuento_interno),
  });
  const buildLineaPostPayload = (l) => ({
    ...buildLineaPayload(l),
    precio_unitario_base: l.precio_unitario_base !== undefined ? parseDecimal(l.precio_unitario_base) : undefined,
    metodo_calculo: l.metodo_calculo || null,
    group_key: l.group_key || null,
    p_3: "",
  });
  const lineaHaCambiado = (original, actual) =>
    JSON.stringify(buildLineaPayload(original)) !== JSON.stringify(buildLineaPayload(actual));

  const buildGrupoPayload = (g) => ({
    metodo_calculo: g.metodo_calculo,
    descripcion_interna: g.descripcion_interna,
    cantidad: parseDecimal(g.cantidad),
    precio_unitario_base: parseDecimal(g.precio_unitario_base),
    descuento_interno: parseDecimal(g.descuento_interno),
    mes_servicio: g.mes_servicio,
    year: g.year,
    mostrar_cuota: g.mostrar_cuota || "SI",
  });
  const grupoHaCambiado = (original, actual) =>
    JSON.stringify(buildGrupoPayload(original)) !== JSON.stringify(buildGrupoPayload(actual));

  const buildGrupoNuevoDetalles = (grupo) => {
    const cantidad = parseDecimal(grupo.cantidad, 1);
    const precioBase = parseDecimal(grupo.precio_unitario_base);
    const descuentoBase = parseDecimal(grupo.descuento_interno);
    const mostrarCuota = grupo.mostrar_cuota || "SI";
    const groupKey = `manual-${grupo.metodo_calculo}-${Date.now()}`;
    return grupo.lineas.map((regla, idx) => ({
      cantidad,
      precio_unitario: Number((precioBase * (Number(regla.porcentaje || 0) / 100)).toFixed(2)),
      descuento_interno: Number((descuentoBase * (Number(regla.porcentaje || 0) / 100)).toFixed(2)),
      id_fac_item: regla.id_fac_item,
      id_regla_detalle: regla.id,
      tipo: regla.id_fac_item,
      detalle: previewDetalleOverrides[`preview-${grupo._localId}-${idx}`] ?? regla.detalle_hippo,
      descripcion_interna: grupo.descripcion_interna.trim(),
      precio_unitario_base: precioBase,
      metodo_calculo: grupo.metodo_calculo,
      group_key: groupKey,
      mostrar_cuota: mostrarCuota,
      mes_servicio: grupo.mes_servicio || DEFAULT_MES,
      year: grupo.year || DEFAULT_YEAR,
    }));
  };

  // Vista previa en vivo de las lineas fiscales de un concepto interno recien
  // agregado (todavia no guardado) — asi "Lineas fiscales para Hippo" se
  // actualiza al tiro al agregar/editar un concepto, sin esperar a guardar.
  const buildGrupoDetallesPreview = (grupo) => {
    const cantidad = parseDecimal(grupo.cantidad, 1);
    const precioBase = parseDecimal(grupo.precio_unitario_base);
    const descuentoBase = parseDecimal(grupo.descuento_interno);
    const mostrarCuota = grupo.mostrar_cuota || "SI";
    return grupo.lineas.map((regla, idx) => {
      const localId = `preview-${grupo._localId}-${idx}`;
      return {
        _localId: localId,
        _esDeGrupo: true,
        _preview: true,
        cantidad: String(cantidad),
        precio_unitario: Number((precioBase * (Number(regla.porcentaje || 0) / 100)).toFixed(2)),
        descuento_interno: Number((descuentoBase * (Number(regla.porcentaje || 0) / 100)).toFixed(2)),
        id_fac_item: regla.id_fac_item,
        tipo: regla.id_fac_item,
        detalle: previewDetalleOverrides[localId] ?? (regla.detalle_hippo || regla.detalle || ""),
        mostrar_cuota: mostrarCuota,
        mes_servicio: grupo.mes_servicio || DEFAULT_MES,
        year: grupo.year || DEFAULT_YEAR,
      };
    });
  };
  // Para conceptos que ya existian: recalcula en vivo el precio/cantidad/periodo
  // de cada linea real (misma identidad — mismo _localId/cod, asi el guardado de
  // "Descripcion Comercial" personalizada sigue funcionando como antes) a partir
  // del estado actual de su concepto interno, usando el porcentaje de su regla.
  const gruposExistentesPorId = new Map(
    grupos.filter((g) => g._origin === "existing").map((g) => [g.id, g])
  );
  const lineasConValoresVigentes = lineas.map((l) => {
    if (!l._esDeGrupo || l._porcentaje === null || l._porcentaje === undefined) return l;
    const grupo = gruposExistentesPorId.get(l._idDetalleGrupo);
    if (!grupo) return l;
    const pct = l._porcentaje / 100;
    return {
      ...l,
      cantidad: String(parseDecimal(grupo.cantidad, 1)),
      precio_unitario: Number((parseDecimal(grupo.precio_unitario_base) * pct).toFixed(2)),
      descuento_interno: Number((parseDecimal(grupo.descuento_interno) * pct).toFixed(2)),
      mes_servicio: grupo.mes_servicio || l.mes_servicio,
      year: grupo.year || l.year,
      mostrar_cuota: grupo.mostrar_cuota || l.mostrar_cuota,
    };
  });
  const lineasVisibles = [
    ...lineasConValoresVigentes,
    ...grupos.filter((g) => g._origin === "new").flatMap(buildGrupoDetallesPreview),
  ];
  const total = lineasVisibles.reduce(
    (acc, l) => acc + ((parseDecimal(l.cantidad) * parseDecimal(l.precio_unitario)) - parseDecimal(l.descuento_interno)), 0);

  const validarAntesDeGuardar = () => {
    if (!headerForm.sucursal) return "La sucursal es requerida";
    for (const g of grupos) {
      if (!g.metodo_calculo) return "Seleccione el ITEM del concepto interno";
      const cantidad = parseDecimal(g.cantidad);
      // Solo se exige precio/cantidad > 0 para conceptos nuevos (los que se
      // agregan en blanco en esta edicion) — no se re-valida esto para
      // conceptos que ya existian, para no bloquear otros cambios por datos
      // previos que nunca pasaron por esta regla.
      if (g._origin === "new") {
        if (!g.descripcion_interna || !g.descripcion_interna.trim()) return "La descripcion interna del concepto es obligatoria";
        if (!Number.isFinite(cantidad) || cantidad <= 0) return "La cantidad del concepto interno debe ser mayor a 0";
        const precioBase = parseDecimal(g.precio_unitario_base);
        if (!Number.isFinite(precioBase) || precioBase <= 0) return "El precio base del concepto interno debe ser mayor a 0";
      }
      const errConcepto = validarDescuentoConceptoInterno(g.descuento_interno, cantidad, g.lineas);
      if (errConcepto) return errConcepto;
    }
    for (const l of lineas) {
      const err = validarDetalleForm(l);
      if (err) return err;
    }
    return null;
  };

  // Arma la lista de operaciones pendientes (sin ejecutarlas).
  const buildCalls = () => {
    const calls = [];

    if (JSON.stringify(headerForm) !== JSON.stringify(headerSnapshotRef.current)) {
      calls.push({ label: "Cabecera", run: () => svc.updateFacturaHeader(cod, headerForm) });
    }

    const lineasActualesById = new Map(lineas.filter((l) => l._origin === "existing").map((l) => [l.cod, l]));
    for (const original of lineasSnapshotRef.current) {
      const actual = lineasActualesById.get(original.cod);
      if (!actual) {
        calls.push({ label: `Eliminar linea #${original.cod}`, run: () => svc.deleteDetalle(cod, original.cod) });
      } else if (lineaHaCambiado(original, actual)) {
        calls.push({ label: `Actualizar linea #${original.cod}`, run: () => svc.updateDetalle(cod, original.cod, buildLineaPayload(actual)) });
      }
    }

    const lineasNuevas = lineas.filter((l) => l._origin === "new");
    if (lineasNuevas.length) {
      calls.push({ label: `Agregar ${lineasNuevas.length} linea(s) nueva(s)`, run: () => svc.addDetalles(cod, lineasNuevas.map(buildLineaPostPayload)) });
    }

    const gruposActualesById = new Map(grupos.filter((g) => g._origin === "existing").map((g) => [g.id, g]));
    for (const original of gruposSnapshotRef.current) {
      const actual = gruposActualesById.get(original.id);
      if (!actual) {
        calls.push({ label: `Eliminar concepto #${original.id}`, run: () => svc.deleteDetalleGrupo(cod, original.id) });
        continue;
      }
      const grupoCambio = grupoHaCambiado(original, actual);
      if (grupoCambio) {
        calls.push({
          label: `Actualizar concepto #${original.id}`,
          run: () => svc.updateDetalleGrupo(cod, original.id, buildGrupoPayload(actual)),
        });
      }
    }

    const gruposNuevos = grupos.filter((g) => g._origin === "new");
    for (const grupo of gruposNuevos) {
      calls.push({ label: `Agregar concepto "${grupo.descripcion_interna}"`, run: () => svc.addDetalles(cod, buildGrupoNuevoDetalles(grupo)) });
    }

    return calls;
  };

  const handleGuardarCambios = async () => {
    const validationError = validarAntesDeGuardar();
    if (validationError) { setError(validationError); return; }
    setError(null);
    setSaveReport(null);
    setSaving(true);

    const calls = buildCalls();

    if (!calls.length) {
      setSaving(false);
      onClose();
      return;
    }

    const results = await Promise.allSettled(calls.map((c) => c.run()));
    const failed = [];
    results.forEach((r, i) => {
      if (r.status === "rejected") {
        failed.push({ label: calls[i].label, message: r.reason?.response?.data?.message || r.reason?.message || "Error desconocido" });
      }
    });

    setSaving(false);
    onSaved?.();

    if (!failed.length) {
      onClose();
    } else {
      setSaveReport({ total: calls.length, failed });
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { m: 0.5, maxHeight: "calc(100% - 8px)" } }}>
      <DialogTitle sx={{ pb: 0.5, fontSize: "0.9rem", fontWeight: 700 }}>
        Editar {factura?.pre_fac === "si" ? "Pre-factura" : "Factura"} #{factura?.coddos || factura?.num_fac || cod}
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ py: 0.5 }}>
        {error && <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>{error}</Alert>}
        {saveReport && (
          <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
            {saveReport.total - saveReport.failed.length} de {saveReport.total} cambios se guardaron. Fallaron:
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
              {saveReport.failed.map((f) => <li key={f.label}>{f.label}: {f.message}</li>)}
            </ul>
            Puedes corregir y volver a presionar Guardar cambios.
          </Alert>
        )}

        {bloqueada && (
          <Alert severity="info" sx={{ mb: 0.5, py: 0.25 }}>
            Esta factura ya fue emitida: solo se puede corregir el Mes/Año y el Tipo de venta de la cabecera.
          </Alert>
        )}
        <Grid container spacing={0.5} sx={{ mb: 0.5 }}>
          <Grid item xs={6} md={1.5}>
            <TextField fullWidth size="small" label="NIT/CI" sx={compactFieldSx}
              value={headerForm.nit || ""} disabled={saving || bloqueada}
              onChange={(e) => setHeaderForm((p) => ({ ...p, nit: e.target.value }))} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Documento" sx={compactFieldSx}
              value={headerForm.tipo_documento_identidad || 5} disabled={saving || bloqueada}
              onChange={(e) => setHeaderForm((p) => ({ ...p, tipo_documento_identidad: Number(e.target.value) }))}>
              {TIPOS_DOCUMENTO.map((t) => <MenuItem key={t.codigo} value={t.codigo}>{t.nombre}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3.5}>
            <TextField fullWidth size="small" label="Nombre / Razón Social a Facturar" sx={compactFieldSx}
              value={headerForm.fac_Nombre || ""} disabled={saving || bloqueada}
              onChange={(e) => setHeaderForm((p) => ({ ...p, fac_Nombre: e.target.value }))} />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField fullWidth size="small" label="Mes" sx={compactFieldSx}
              inputProps={{ maxLength: 100 }}
              value={headerForm.mes || ""} disabled={saving}
              onChange={(e) => setHeaderForm((p) => ({ ...p, mes: e.target.value }))} />
          </Grid>
          <Grid item xs={6} md={1}>
            <TextField fullWidth size="small" label="Año" type="text" sx={compactFieldSx}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              value={headerForm.year || ""} disabled={saving}
              onChange={(e) => setHeaderForm((p) => ({ ...p, year: e.target.value }))} />
          </Grid>
          <Grid item xs={6} md={1.25}>
            <TextField select fullWidth size="small" label="Sucursal" sx={compactFieldSx}
              value={headerForm.sucursal || ""} disabled={saving || bloqueada}
              onChange={(e) => setHeaderForm((p) => ({ ...p, sucursal: e.target.value }))}>
              {SUCURSALES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} md={1.25}>
            <TextField select fullWidth size="small" label="Tipo venta" sx={compactFieldSx}
              value={headerForm.venta || "Alquiler"} disabled={saving}
              onChange={(e) => setHeaderForm((p) => ({ ...p, venta: e.target.value }))}>
              {VENTAS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>

        {/* Conceptos internos */}
        {(tieneDetalleInterno || (!lineas.length && tieneReglasInternasConfiguradas)) && (
          <Paper variant="outlined" sx={{ mb: 0.5 }}>
            <Box sx={{ p: 0.5, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: "warning.main" }}>
                CONCEPTOS INTERNOS ({grupos.length})
              </Typography>
              {!bloqueada && (
                <Tooltip title={!conceptosDisponiblesNuevos.length ? "No hay reglas internas configuradas para este servicio" : "Agregar fila en blanco para elegir el tipo ahi mismo"}>
                  <span>
                    <Button size="small" variant="text" startIcon={<AddIcon />} onClick={handleAgregarConceptoBlanco} disabled={saving || !conceptosDisponiblesNuevos.length}>
                      Agregar detalle
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
            <TableContainer>
              <Table size="small" sx={compactTableSx}>
                <TableHead>
                  <TableRow>
                    <Th align="center">Cant.</Th>
                    <Th>Descripcion interna</Th>
                    <Th align="right">P. Base</Th>
                    <Th align="right">Descuento</Th>
                    <Th align="center">Periodo</Th>
                    <Th align="right">Total</Th>
                    <Th align="center">X</Th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grupos.length ? grupos.map((g) => (
                    <GrupoRow key={g._localId} g={g} conceptos={conceptosDisponiblesNuevos}
                      onChange={updateGrupoField} onTipoChange={handleGrupoTipoChange} onDelete={removeGrupo}
                      disabled={saving || bloqueada} />
                  )) : (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 1.5, color: "text.secondary" }}>
                      Sin conceptos internos. Use Agregar detalle para crear el primero.
                    </TableCell></TableRow>
                  )}
                  {grupos.length > 0 && (
                    <TableRow sx={{ bgcolor: (theme) => theme.palette.action.hover }}>
                      <TableCell colSpan={5} align="right" sx={{ fontWeight: 800 }}>TOTAL CONCEPTOS INTERNOS</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>{money(totalConceptosInternos)}</TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Lineas de detalle */}
        <Paper variant="outlined">
          <Box sx={{ p: 0.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
              {tieneDetalleInterno ? "LINEAS FISCALES PARA HIPPO" : "LINEAS DE DETALLE"} ({lineasVisibles.length})
            </Typography>
            {!bloqueaLineasFiscales && !addingLinea && (lineas.some((l) => !l._esDeGrupo) || !tieneReglasInternasConfiguradas) && (
              <Button size="small" variant="text" startIcon={<AddIcon />} onClick={startAddLinea} disabled={saving}>Agregar linea</Button>
            )}
          </Box>
          <TableContainer>
            <Table size="small" sx={compactTableSx}>
              <TableHead>
                <TableRow>
                  <Th align="center">Cant.</Th>
                  <Th>Item</Th>
                  <Th>Descripción Comercial</Th>
                  <Th align="center">Cuota?</Th>
                  <Th>Periodo</Th>
                  <Th align="right">P. Unit</Th>
                  <Th align="right">Desc.</Th>
                  <Th align="right">Subtotal</Th>
                  <Th align="center">X</Th>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineasVisibles.map((l) => (
                  <LineaRow key={l._localId} l={l} facItems={facItems}
                    onChange={updateLineaField} onTipoChange={handleLineaTipoChange} onDelete={removeLinea}
                    disabled={saving || (l._esDeGrupo ? bloqueada : bloqueaLineasFiscales)}
                    soloDetalle={l._esDeGrupo} />
                ))}
                {addingLinea && (
                  <TableRow sx={{ bgcolor: "rgba(76, 175, 80, 0.04)" }}>
                    <TableCell>
                      <TextField size="small" type="text" sx={{ ...compactFieldSx, width: 60 }}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        value={nuevaLinea.cantidad} disabled={saving}
                        onChange={(e) => { if (allowIntegerInput(e.target.value)) setNuevaLinea((p) => ({ ...p, cantidad: e.target.value })); }} />
                    </TableCell>
                    <TableCell>
                      <TextField select size="small" sx={{ ...compactFieldSx, width: 112 }}
                        value={String(nuevaLinea.id_fac_item || nuevaLinea.tipo || "")} required error={!nuevaLinea.id_fac_item} disabled={saving}
                        onChange={(e) => handleNuevaLineaTipoChange(e.target.value)}>
                        <MenuItem value="">Seleccione</MenuItem>
                        {facItems.map((item) => <MenuItem key={getItemValue(item)} value={getItemValue(item)}>{getItemLabel(item)}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField size="small" fullWidth sx={compactFieldSx} placeholder="Detalle descriptivo"
                        value={nuevaLinea.detalle} disabled={saving}
                        onChange={(e) => setNuevaLinea((p) => ({ ...p, detalle: e.target.value }))} />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox size="small" sx={{ p: 0 }}
                        checked={(nuevaLinea.mostrar_cuota || "SI") === "SI"} disabled={saving}
                        onChange={(e) => setNuevaLinea((p) => ({ ...p, mostrar_cuota: e.target.checked ? "SI" : "NO" }))} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.3 }}>
                        <TextField select size="small" sx={{ ...compactFieldSx, width: 85 }}
                          value={nuevaLinea.mes_servicio || ""} disabled={saving}
                          onChange={(e) => setNuevaLinea((p) => ({ ...p, mes_servicio: e.target.value }))}>
                          {MESES_STR.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                        </TextField>
                        <TextField size="small" type="text" sx={{ ...compactFieldSx, width: 55 }}
                          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                          value={nuevaLinea.year || ""} disabled={saving}
                          onChange={(e) => setNuevaLinea((p) => ({ ...p, year: e.target.value }))} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="text" sx={{ ...compactFieldSx, width: 80 }}
                        inputProps={{ inputMode: "decimal" }}
                        value={nuevaLinea.precio_unitario} disabled={saving}
                        onChange={(e) => { if (allowPriceInput(e.target.value)) setNuevaLinea((p) => ({ ...p, precio_unitario: e.target.value })); }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="text" sx={{ ...compactFieldSx, width: 75 }}
                        inputProps={{ inputMode: "decimal" }}
                        value={nuevaLinea.descuento_interno} disabled={saving}
                        onChange={(e) => { if (allowPriceInput(e.target.value)) setNuevaLinea((p) => ({ ...p, descuento_interno: e.target.value })); }} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {Number((parseDecimal(nuevaLinea.cantidad) * parseDecimal(nuevaLinea.precio_unitario)) - parseDecimal(nuevaLinea.descuento_interno)).toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.2 }}>
                        <IconButton size="small" color="success" onClick={handleAddLinea} disabled={saving}><AddIcon fontSize="inherit" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => setAddingLinea(false)} disabled={saving}><DeleteIcon fontSize="inherit" /></IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow sx={{ bgcolor: (theme) => theme.palette.action.hover }}>
                  <TableCell colSpan={7} align="right" sx={{ fontWeight: 700 }}>TOTAL {headerForm.venta ? "" : ""}:</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, color: "primary.main" }}>{total.toFixed(2)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ p: 1 }}>
        <Button onClick={onClose} size="small" variant="outlined" color="inherit" disabled={saving}>Cancelar</Button>
        <Button onClick={handleGuardarCambios} size="small" variant="contained" color="primary" disabled={saving}>
          {saving ? "Guardando..." : "Guardar cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditarFacturaModal;
